import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ComponentClient, ComponentDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ComponentClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useComponents = () => {
    return useQuery({
        queryKey: ['components'],
        queryFn: async () => {
            const response = await apiClient.searchComponents({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
                sortDescending: false,
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useComponentSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchComponents(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useComponentById = (id: string) => {
    return useQuery({
        queryKey: ['component', id],
        queryFn: async () => {
            const response = await apiClient.getComponentById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Query statuses by component ID
export const useComponentStatusesById = (id: string) => {
    return useQuery({
        queryKey: ['componentStatuses', id],
        queryFn: async () => {
            const response = await apiClient.getComponentStatuses(id);
            return response?.data || [];
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateComponent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ComponentDTO) => {
            return await apiClient.createComponent(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['components'] });
        },
    });
};

// Update mutation
export const useUpdateComponent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: ComponentDTO }) => {
            return await apiClient.updateComponent(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['components'] });
        },
    });
};

// Delete mutation
export const useDeleteComponent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteComponent(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['components'] });
        },
    });
};

// Direct statuses fetch helper
export const useComponentStatuses = () => {
    const getStatuses = async (id: string) => {
        const response = await apiClient.getComponentStatuses(id);
        return response?.data || [];
    };

    return { getStatuses };
};